<template>
  <div v-if="isAIFeaturesEnabled">
    <b-modal
      id="content-generator-modal"
      ref="contentGeneratorModal"
      title="สร้างเนื้อหาโซเชียลมีเดีย"
      size="lg"
      hide-footer
      @hidden="resetModal"
    >
    <!-- Header with recommendation title -->
    <div class="recommendation-header mb-3 p-3 bg-light rounded">
      <h6 class="mb-1 text-primary">
        <i class="fas fa-lightbulb mr-2"></i>
        คำแนะนำ: {{ recommendation.title }}
      </h6>
      <small class="text-muted">{{ recommendation.description }}</small>
    </div>

    <!-- Loading State -->
    <div v-if="isGenerating" class="text-center py-5">
      <div class="ai-pulse mb-3"></div>
      <h5>AI กำลังสร้างเนื้อหา...</h5>
      <p class="text-muted">กรุณารอสักครู่</p>
    </div>

    <!-- Both Platform Contents -->
    <div v-else>
      <b-row>
        <!-- LINE Broadcast Column -->
        <b-col md="6">
          <div class="content-section">
            <div class="platform-info mb-3 p-2 bg-success text-white rounded d-flex justify-content-between align-items-center">
              <div>
                <i class="fab fa-line mr-2"></i>
                <strong>LINE Broadcast Message</strong>
              </div>
              <CopyToClipboard 
                v-if="generatedContent.line"
                :text="getLineContent()" 
                class="copy-on-header"
              />
            </div>
            
            <div v-if="generatedContent.line">
              <!-- Message Content -->
              <div class="content-block mb-3">
                <label class="font-weight-bold">ข้อความหลัก:</label>
                <div class="content-preview p-3 border rounded bg-white mt-2">
                  <div v-html="formatLineMessage(generatedContent.line.message)"></div>
                </div>
              </div>

              <!-- Call to Action -->
              <div class="content-block mb-3" v-if="generatedContent.line.callToAction">
                <label class="font-weight-bold">Call to Action:</label>
                <div class="content-preview p-2 border rounded bg-light mt-2">
                  {{ generatedContent.line.callToAction }}
                </div>
              </div>

            </div>
            
            <div v-else class="text-center py-4">
              <p class="text-muted">ยังไม่มีเนื้อหา LINE ที่สร้างขึ้น</p>
            </div>
          </div>
        </b-col>

        <!-- Facebook Post Column -->
        <b-col md="6">
          <div class="content-section">
            <div class="platform-info mb-3 p-2 bg-primary text-white rounded d-flex justify-content-between align-items-center">
              <div>
                <i class="fab fa-facebook mr-2"></i>
                <strong>Facebook Post</strong>
              </div>
              <CopyToClipboard 
                v-if="generatedContent.facebook"
                :text="getFacebookContent()" 
                class="copy-on-header"
              />
            </div>
            
            <div v-if="generatedContent.facebook">
              <!-- Post Content -->
              <div class="content-block mb-3">
                <label class="font-weight-bold">เนื้อหาโพสต์:</label>
                <div class="content-preview p-3 border rounded bg-white mt-2">
                  <div v-html="formatFacebookPost(generatedContent.facebook.post)"></div>
                </div>
              </div>

              <!-- Hashtags -->
              <div class="content-block mb-3" v-if="generatedContent.facebook.hashtags">
                <label class="font-weight-bold">Hashtags:</label>
                <div class="content-preview p-2 border rounded bg-light mt-2">
                  <span class="hashtag-preview">{{ generatedContent.facebook.hashtags }}</span>
                </div>
              </div>

            </div>
            
            <div v-else class="text-center py-4">
              <p class="text-muted">ยังไม่มีเนื้อหา Facebook ที่สร้างขึ้น</p>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Regenerate Button -->
    <div v-if="hasGeneratedContent && !isGenerating" class="text-center mt-4 pt-3 border-top">
      <b-button 
        variant="outline-secondary" 
        @click="generateContent"
      >
        <i class="fas fa-redo mr-2"></i>
        สร้างเนื้อหาใหม่
      </b-button>
    </div>

    </b-modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import CopyToClipboard from '@/components/CopyToClipboard.vue';
import { isAIFeaturesEnabled } from '@/utils/environmentCheck';

export default {
  name: 'ContentGenerator',
  components: {
    CopyToClipboard
  },
  
  props: {
    recommendation: {
      type: Object,
      default: () => ({})
    },
    clinicUrl: {
      type: String,
      required: true
    },
    branchUrl: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      isGenerating: false,
      generatedContent: {
        line: null,
        facebook: null
      }
    };
  },

  computed: {
    hasGeneratedContent() {
      const hasContent = this.generatedContent.line || this.generatedContent.facebook;
      console.log('hasGeneratedContent:', hasContent);
      console.log('Current generatedContent:', this.generatedContent);
      return hasContent;
    },
    isAIFeaturesEnabled() {
      return isAIFeaturesEnabled();
    }
  },

  methods: {
    ...mapActions({
      generateSocialContentAction: 'moduleAiAnalysis/generateSocialContent'
    }),

    show() {
      this.$refs.contentGeneratorModal.show();
      // Auto-generate content for both platforms when modal opens
      this.$nextTick(() => {
        this.generateContent();
      });
    },

    hide() {
      this.$refs.contentGeneratorModal.hide();
    },

    resetModal() {
      this.isGenerating = false;
      this.generatedContent = {
        line: null,
        facebook: null
      };
    },

    async generateContent() {
      if (!this.recommendation.title) {
        this.$toast.error('ไม่มีข้อมูลคำแนะนำ');
        return;
      }

      this.isGenerating = true;
      
      try {
        const response = await this.generateSocialContentAction({
          recommendation: this.recommendation,
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl
        });

        console.log('Full response:', response);
        console.log('Response data:', response?.data);
        console.log('Response content:', response?.data?.content);

        if (response?.data?.content) {
          this.generatedContent = response.data.content;
          console.log('Set generatedContent:', this.generatedContent);
        } else {
          console.log('No content found in response structure');
        }
      } catch (error) {
        console.error('Error generating social content:', error);
        this.$toast.error('เกิดข้อผิดพลาดในการสร้างเนื้อหา');
      } finally {
        this.isGenerating = false;
      }
    },

    formatLineMessage(message) {
      if (!message) return '';
      return message.replace(/\n/g, '<br>');
    },

    formatFacebookPost(post) {
      if (!post) return '';
      return post.replace(/\n/g, '<br>');
    },

    getLineContent() {
      const content = this.generatedContent.line;
      if (!content) return '';

      let fullContent = content.message || '';
      if (content.callToAction) {
        fullContent += '\n\n' + content.callToAction;
      }
      return fullContent;
    },

    getFacebookContent() {
      const content = this.generatedContent.facebook;
      if (!content) return '';

      let fullContent = content.post || '';
      if (content.hashtags) {
        fullContent += '\n\n' + content.hashtags;
      }
      return fullContent;
    }
  }
};
</script>

<style scoped>
.recommendation-header {
  border-left: 4px solid #007bff;
}

.content-section {
  min-height: 300px;
}

.platform-info {
  border-radius: 8px;
}

.content-block {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
}

.content-preview {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  line-height: 1.6;
  white-space: pre-wrap;
}

.hashtag-preview {
  color: #1da1f2;
  font-weight: 500;
}

/* AI Loading Animation */
.ai-pulse {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
  margin: 0 auto;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.5;
  }
}

/* Button Styling */
.btn-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  transition: all 0.3s ease;
}

.btn-gradient-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
}



/* Copy on Header Styling - Only for ContentGenerator Modal */
.platform-info .copy-on-header {
  background-color: rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  padding: 2px;
  transition: all 0.3s ease;
}

.platform-info .copy-on-header:hover {
  background-color: rgba(0, 0, 0, 0.25);
  transform: scale(1.05);
}

.platform-info .copy-on-header >>> .copy-to-clipboard-wrapper {
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
}

.platform-info .copy-on-header >>> .copy-to-clipboard-wrapper:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
}

.platform-info .copy-on-header >>> .far.fa-clone {
  color: white !important;
  font-size: 14px !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.platform-info .copy-on-header >>> .copy-to-clipboard-wrapper:hover .far.fa-clone {
  color: #f8f9fa !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .content-block {
    padding: 10px;
  }
  
  .platform-info {
    text-align: center;
  }
}
</style>